import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  Radio,
  Rate,
  Select,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BagIcon from "../../../assets/icons/BagIcon";
import ClockCircleIcon from "../../../assets/icons/ClockCircleIcon";
import DownArrowIcon from "../../../assets/icons/DownArrowIcon";
import FlyIcon from "../../../assets/icons/FlyIcon";
import LocationIcon from "../../../assets/icons/LocationIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import VerifyIcon from "../../../assets/icons/VerifyIcon";
import UserImage from "../../../assets/images/avatar.svg";
import IMG from "../../../assets/images/congratulations.svg";
import CongratulationsModal from "../../../components/common/CongratulationsModal";
import TitleBar from "../../../components/common/TitleBar";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomModal from "../../../components/ui/CustomModal";
import Loader from "../../../components/ui/Loader";
import { useGetAllKPIGroupQuery } from "../../../features/organization/contractual/KPISetup/kpiSetupApi";
import { useGetAllSalarySlabQuery } from "../../../features/organization/contractual/SalarySlab/salarySlabApi";
import {
  useCloseJobPostMutation,
  useGetSingleJobAppliedQuery,
} from "../../../features/organization/job/jobApi";
import { useSendToPaymentMutation } from "../../../features/organization/payment/paymentApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate, formatShortRelativeTime } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { underscoreToCapitalizeString } from "../../../utils/utils";
import { validatePositiveNumber } from "../../../utils/validators";
import NotFound from "../../NotFound";
import AssignmentModal from "./_components/AssignmentModal";

type MODAL_TYPE =
  | "CLOSE"
  | "FEEDBACK"
  | "CONGRATULATIONS"
  | "PAYMENT"
  | "ASSIGNMENT";
const backLink = "/organization/my-projects";

const options = [
  { label: "The Best", value: "best", rating: 5 },
  { label: "Good", value: "good", rating: 4 },
  { label: "Average", value: "average", rating: 3 },
  { label: "Poor", value: "poor", rating: 2 },
  { label: "Very Bad", value: "very_bad", rating: 1 },
];

export type AssignmentDataType = {
  data?:
    | {
        content: string;
        remarks: string | null;
        reviewed_at: string | null;
        status: "pending" | "approved" | "rejected";
        type: "text" | "file";
      }[]
    | null;
  status?: string;
  remarks?: string;
  id: number;
};

export default function MyProjectDetails() {
  const [form] = Form.useForm();
  const { open, handleOpen, handleClose } = useModal<
    AssignmentDataType,
    MODAL_TYPE
  >();
  const [showFull, setShowFull] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState<
    string | number | (string | number)[]
  >("1");
  const { id } = useParams();
  const [
    sendToPayment,
    {
      isLoading: isSending,
      isError: isSendError,
      error: sendError,
      isSuccess: isSendSuccess,
    },
  ] = useSendToPaymentMutation();

  // Queries
  const {
    data,
    isLoading: isJobLoading,
    isError: isJobError,
    error: jobError,
  } = useGetSingleJobAppliedQuery(Number(id), {
    skip: !id,
  });

  const singleData = data?.data;
  const closed = singleData?.jobApplication?.is_closed;
  const submitted = singleData?.jobApplication?.is_submitted_task;
  const canClose = !closed && !!submitted;
  const isSendPayment = !!singleData?.jobApplication?.is_payment_created;
  // Mutations
  const [closeJob, { isLoading, isError, isSuccess, error }] =
    useCloseJobPostMutation();

  const { data: allSalarySlabData, isLoading: allSalarySlabIsLoading } =
    useGetAllSalarySlabQuery({
      params: {
        page: 1,
        limit: 10000000000,
        status: "active",
      },
    });

  const {
    data: allKpiGroupData,
    isLoading: allKpiGroupIsLoading,
    isError: allKpiGroupIsError,
    error: allKpiGroupError,
  } = useGetAllKPIGroupQuery({
    params: {
      page: 1,
      limit: 10000000000,
      status: "active",
      for: "assign-kip",
    },
  });

  useError(isJobError, jobError, "Job fetch fail");
  useError(isSendError, sendError, "Payment send fail");
  useError(allKpiGroupIsError, allKpiGroupError, "Failed to fetch KPI groups");

  const handleChange = (key: any) => {
    setActiveKey(key);
  };

  const handleFinish = (values: any) => {
    if (!canClose) return;
    closeJob({ id, data: values });
  };

  const onFinish = (values: any) => {
    // handleFinish(values);
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    const { joining_date, contract_months } = allValues;

    if (joining_date && contract_months && contract_months > 0) {
      const endDate = dayjs(joining_date).add(contract_months, "month");
      form.setFieldsValue({ contract_end_date: endDate });
    } else {
      // Clear contract_end_date when either value is missing or invalid
      form.setFieldsValue({ contract_end_date: null });
    }
  };

  const disablePastDates = (current: any) =>
    current && current < dayjs().startOf("day");

  const handleSlabChange = (value: any) => {
    if (!value) {
      // When cleared
      form.setFieldsValue({
        fixed_amount: undefined,
        variable_amount: undefined,
      });
      return;
    }

    const selectedSlab = allSalarySlabData?.data?.salarySlabs?.find(
      (item: any) => item.id === value
    );

    if (selectedSlab) {
      form.setFieldsValue({
        fixed_amount: selectedSlab.fixed_amount,
        variable_amount: selectedSlab.variable_amount,
      });
    }
  };

  useEffect(() => {
    const jobApp = singleData?.jobApplication;
    const assignedSlab = jobApp?.active_assigned_slab;
    const assignedKpi = jobApp?.active_assigned_kpi_group;
    const contract = jobApp?.active_contract;

    if (
      assignedSlab?.salary_slab_id &&
      assignedKpi?.kpi_group_id &&
      contract?.contract_months &&
      contract?.payment_type &&
      contract?.contract_start_date &&
      contract?.contract_end_date
    ) {
      form.setFieldsValue({
        slab_id: assignedSlab.salary_slab_id,
        fixed_amount: assignedSlab.salary_slab?.fixed_amount,
        variable_amount: assignedSlab.salary_slab?.variable_amount,
        kpi_group_id: assignedKpi.kpi_group_id,
        contract_months: contract.contract_months,
        payment_type: contract.payment_type,
        joining_date: dayjs(contract.contract_start_date),
        contract_end_date: dayjs(contract.contract_end_date),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    singleData?.jobApplication?.active_assigned_slab,
    singleData?.jobApplication?.active_assigned_kpi_group,
    singleData?.jobApplication?.active_contract,
  ]);

  // Errors
  useError(isError, error, "Job close failed.");

  useEffect(() => {
    if (isSuccess && !isLoading) {
      handleOpen({ type: "CONGRATULATIONS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);
  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      const isClamped = el?.scrollHeight > el?.clientHeight;
      setIsOverflowing(isClamped);
    }
  }, [singleData?.jobApplication?.coverLetter]);

  useEffect(() => {
    if (isSendSuccess) {
      toast("success", "Payment sent successfully");
      navigate("/organization/payment");
    }
  }, [isSendSuccess, navigate]);

  if (
    jobError &&
    typeof jobError === "object" &&
    "status" in jobError &&
    (jobError as { status?: number }).status === 404
  ) {
    return <NotFound />;
  }

  return (
    <SidebarLayout title="My Projects">
      {open?.type === "PAYMENT" && !isSendPayment && (
        <ConfirmModal
          open={open.open}
          data={open.data}
          handleClose={handleClose}
          title="Send to Payment"
          subTitle="Are you sure you want to proceed with the payment page?"
          okText="Confirm"
          handleOk={() => {
            if (id) sendToPayment({ id });
          }}
          okLoading={isSending}
        />
      )}
      {open?.type === "ASSIGNMENT" &&
        singleData?.jobApplication?.assignment?.length && (
          <AssignmentModal
            open={open.open}
            data={{
              data: singleData?.jobApplication?.assignment,
              status: singleData?.jobApplication?.assignment_status,
              remarks: singleData?.jobApplication?.assignment_remarks,
              id: Number(id),
            }}
            handleClose={handleClose}
          />
        )}

      {isJobLoading ? (
        <Loader />
      ) : (
        <>
          <TitleBar title={`Project Details - ${id}`} backLink={backLink} />
          <div className="grid md:grid-cols-12 gap-[18px]">
            {/* Left Side – 60% */}
            <div className="md:col-span-7">
              {/* Proposal Details Card */}
              <Card>
                <div className="flex items-start justify-between border-b border-[#D7DFE9] pb-2 mb-2">
                  <div className="flex items-start justify-start gap-4">
                    <div>
                      <img
                        className="w-11 h-11 rounded-[100px]"
                        src={
                          singleData?.jobApplication?.applicant?.profile
                            ?.pro_pic || UserImage
                        }
                        alt="user_image"
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-x-4 flex-wrap">
                        <h4 className="text-base font-medium text-header">
                          {singleData?.jobApplication?.applicant?.name}
                        </h4>
                        {singleData?.jobApplication?.applicant?.is_verified ===
                          1 && (
                          <span className="text-xs font-medium flex items-center gap-1.5 text-primary">
                            <VerifyIcon />
                            Verified
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-sm text-body-text font-medium my-1">
                        <LocationIcon />
                        {singleData?.jobApplication?.applicant?.profile
                          ?.district || "N/A"}
                      </div>

                      <div className="flex items-center flex-wrap gap-x-3">
                        <Rate
                          disabled
                          allowHalf
                          value={0}
                          style={{ color: "#FFC700" }}
                        />

                        {/* Todo: Need to show rating */}
                        {/* <div>
                      <span className="text-header text-base font-medium inline-block mr-1">
                        4.8
                      </span>
                      <span className="text-header text-sm font-normal inline-block">
                        (251)
                      </span>
                    </div> */}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-body-text font-medium">
                    Mobile:{" "}
                    {singleData?.jobApplication?.applicant?.mobile || "N/A"}
                  </div>

                  {/* <div>
                    <Button
                      size="small"
                      className="w-full !bg-[#11A75C] !text-white border border-[#11A75C] hover:!bg-[#11A75C] hover:!border-[#11A75C]"
                      icon={<DoubleMessageIcon />}
                    >
                      Message
                    </Button>
                  </div> */}
                </div>

                <div className="flex items-start justify-between">
                  <div>
                    <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                      {singleData?.jobApplication?.created_at
                        ? formatDate(singleData?.jobApplication?.created_at)
                        : "-"}
                    </div>
                    <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                      {singleData?.jobApplication?.created_at
                        ? formatShortRelativeTime(
                            singleData?.jobApplication?.created_at
                          )
                        : "-"}
                    </div>
                  </div>

                  {singleData?.jobApplication?.job?.job_category ===
                  "call_center" ? null : (
                    <div>
                      <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                        Price
                      </div>
                      <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                        {singleData?.jobApplication?.price}/
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type ?? ""
                        )}
                      </div>
                    </div>
                  )}

                  {!!singleData?.jobApplication?.final_price && (
                    <div>
                      <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                        Final Price
                      </div>
                      <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                        {singleData?.jobApplication?.final_price}/
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type ?? ""
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="self-stretch justify-start text-slate-500 text-xs font-normal">
                      Location
                    </div>
                    <div className="self-stretch justify-start text-zinc-700 text-sm font-bold">
                      {singleData?.jobApplication?.location}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Cover Letter Card */}
              <Card className="mt-4">
                <div className="text-zinc-700 text-lg font-medium mb-4">
                  Cover Letter
                </div>

                <div
                  ref={contentRef}
                  className={`text-slate-700 text-sm font-normal whitespace-pre-line transition-all duration-300 ${
                    showFull ? "" : "line-clamp-6"
                  }`}
                >
                  {singleData?.jobApplication?.coverLetter || "N/A"}
                </div>

                {isOverflowing && (
                  <div
                    onClick={() => setShowFull(!showFull)}
                    className="mt-2 cursor-pointer text-blue-600 text-sm font-normal w-fit"
                  >
                    {showFull ? "View Less" : "View More"}
                  </div>
                )}
              </Card>

              {/* Additional Questions Card */}
              {/* Using Collapse for additional questions */}
              <Card className="mt-4">
                <div className="text-zinc-700 text-lg font-medium mb-4">
                  Additional Questions
                </div>

                {singleData?.jobApplication?.questionsAnswers?.map(
                  (item, index) => (
                    <div className="mt-2 mb-2" key={index}>
                      <Collapse
                        activeKey={activeKey}
                        onChange={handleChange}
                        expandIconPosition="end"
                        expandIcon={({ isActive }) =>
                          isActive ? (
                            <span
                              style={{
                                transform: isActive
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                              }}
                            >
                              <DownArrowIcon />
                            </span>
                          ) : (
                            <DownArrowIcon />
                          )
                        }
                        items={[
                          {
                            key: `${index + 1}`,
                            label: item?.question,
                            children: <p>{item?.answer}</p>,
                          },
                        ]}
                      />
                    </div>
                  )
                )}
              </Card>

              {/* Slot Card */}
              {/* This card is for displaying the slot details */}
              {/* Todo: Need */}
              {/* <Card className="mt-4">
                <div className="justify-center text-zinc-700 text-lg font-medium mb-2">
                  Slot
                </div>

                <div className="flex justify-between items-start">
                  <div>
                    <div className="justify-center text-slate-700 text-sm font-normal">
                      Date
                    </div>
                    <div className="justify-start text-zinc-700 text-sm font-medium">
                      22 May, 2025
                    </div>
                  </div>
                  <div>
                    <div className="justify-center text-slate-700 text-sm font-normal">
                      Slot
                    </div>
                    <div className="justify-start text-zinc-700 text-sm font-medium">
                      9:00 AM - 12:00 PM
                    </div>
                  </div>

                  <div>
                    <div className="justify-center text-slate-700 text-sm font-normal">
                      Seat
                    </div>
                    <div className="justify-start text-zinc-700 text-sm font-medium">
                      Seat 6
                    </div>
                  </div>
                </div>
              </Card> */}
            </div>

            {/* Right Side – 40% */}
            <div className="md:col-span-5">
              {/* Job Details Card */}
              <Card className="mb-4">
                <div className="text-zinc-700 text-lg font-medium border-b border-[#D7DFE9] pb-2 mb-2 !inline-block break-all whitespace-normal max-w-full">
                  Job Details
                </div>

                <div className="text-blue-600 text-sm font-medium mb-2">
                  {singleData?.jobApplication?.job?.job_title}
                </div>

                <div className="flex items-center justify-start gap-3">
                  <div>
                    <BagIcon />
                  </div>
                  <div className="text-slate-600 text-xs font-normal">
                    {underscoreToCapitalizeString(
                      singleData?.jobApplication?.job?.job_category ?? ""
                    )}
                  </div>
                  {singleData?.jobApplication?.job?.job_type && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        {singleData?.jobApplication?.job?.job_type}
                      </div>
                    </>
                  )}
                  {singleData?.jobApplication?.job?.contract_type ===
                    "fixed_term" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT{" "}
                        {
                          singleData?.jobApplication?.job
                            ?.fixed_term_price_per_month
                        }{" "}
                        - {singleData?.jobApplication?.job?.fixed_term_duration}{" "}
                        /{" "}
                        {underscoreToCapitalizeString(
                          singleData?.jobApplication?.job?.contract_type
                        )}
                      </div>
                    </>
                  )}
                  {singleData?.jobApplication?.job?.contract_type ===
                    "hourly" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT {singleData?.jobApplication?.job?.hourly_price} /{" "}
                        {underscoreToCapitalizeString(
                          String(
                            singleData?.jobApplication?.job?.contract_type ?? ""
                          )
                        )}
                      </div>
                    </>
                  )}
                  {singleData?.jobApplication?.job?.contract_type ===
                    "open_bid" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT{" "}
                        {singleData?.jobApplication?.job?.open_bid_max_budget} /{" "}
                        {underscoreToCapitalizeString(
                          String(
                            singleData?.jobApplication?.job?.contract_type ?? ""
                          )
                        )}
                      </div>
                    </>
                  )}

                  {singleData?.jobApplication?.job?.contract_type ===
                    "milestone" && (
                    <>
                      <div className="text-xs text-slate-400">|</div>
                      <div className="text-slate-600 text-xs font-normal">
                        BDT {singleData?.jobApplication?.job?.milestone_price} /{" "}
                        {underscoreToCapitalizeString(
                          String(
                            singleData?.jobApplication?.job?.contract_type ?? ""
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-start gap-3 mt-2">
                  <div>
                    <FlyIcon />
                  </div>
                  <div className="justify-center text-slate-600 text-xs font-normal">
                    Proposals:{" "}
                    {singleData?.jobApplication?.job
                      ?.number_of_people_applied || 0}
                  </div>
                </div>

                {/* <div className="flex items-center justify-start gap-3 mt-2">
                  <div>
                    <PayCardIcon />
                  </div>
                  <div className="justify-center text-slate-600 text-xs font-normal">
                    Payment verified
                  </div>
                  <div>
                    <GreenCircleCorrectIcon />
                  </div>
                </div> */}

                <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4">
                  {singleData?.jobApplication?.job?.job_description}
                </div>

                <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4">
                  {singleData?.jobApplication?.job?.penalty_clause}
                </div>

                <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4">
                  <span className="font-medium">Requirement Skills:</span>{" "}
                  {singleData?.jobApplication?.job?.skills?.join(", ")}
                </div>

                <div className="flex items-center justify-start gap-3 mt-3 mb-3">
                  <div>
                    <ClockCircleIcon />
                  </div>
                  <div className="justify-center text-slate-600 text-xs font-normal">
                    Posted{" "}
                    {singleData?.jobApplication?.job?.created_at
                      ? formatShortRelativeTime(
                          singleData?.jobApplication?.job?.created_at
                        )
                      : "-"}
                  </div>
                </div>
                {singleData?.jobApplication?.job?.survey_template_id ? (
                  <Link
                    to={`/organizations/survey/templates/survey-data-details/${singleData?.jobApplication?.job?.survey_template_id}/${singleData?.jobApplication.business_member_id}?bondhu=${singleData?.jobApplication?.applicant?.name}`}
                  >
                    <Button
                      size="large"
                      className="w-full bg-[#FBF4FF] !text-[#B721FF] border:none hover:bg-[#FBF4FF] hover:border-[#B721FF]"
                    >
                      View Survey
                    </Button>
                  </Link>
                ) : singleData?.jobApplication?.job?.job_category ===
                  "remote_work" ? (
                  <Button
                    onClick={() => {
                      handleOpen({
                        type: "ASSIGNMENT",
                      });
                    }}
                    size="large"
                    className="w-full bg-[#FBF4FF] disabled:bg-[#FBF4FF]/50 disabled:border-[#d0d0d0] disabled:!text-[#B721FF]/50 !text-[#B721FF] hover:bg-[#FBF4FF] hover:border-[#B721FF] border-[#B721FF]"
                    disabled={!singleData?.jobApplication?.assignment}
                  >
                    Assignment
                  </Button>
                ) : singleData?.jobApplication?.job?.job_category ===
                  "call_center" ? (
                  <Link
                    to={`${singleData?.jobApplication?.business_member_id}`}
                  >
                    <Button
                      size="large"
                      className="mt-4 w-full bg-[#FBF4FF] disabled:bg-[#FBF4FF]/50 disabled:border-[#d0d0d0] disabled:!text-[#B721FF]/50 !text-[#B721FF] hover:bg-[#FBF4FF] hover:border-[#B721FF] border-[#B721FF]"
                    >
                      View Details
                    </Button>
                  </Link>
                ) : (
                  ""
                )}
              </Card>

              {/* Deals Card */}
              {/* This card is for confirming the hire and finalizing the deal */}
              <Form
                className="custom-form"
                layout="vertical"
                requiredMark="optional"
                size="large"
                onFinish={onFinish}
                form={form}
                onValuesChange={handleValuesChange}
              >
                <Card>
                  <div className="text-zinc-700 text-lg font-medium border-b border-[#D7DFE9] pb-2 mb-2">
                    Deals
                  </div>

                  {singleData?.jobApplication?.job?.job_category ===
                  "call_center" ? (
                    <div>
                      <Form.Item
                        name="slab_id"
                        label="Salary Slab"
                        className="mb-4"
                        rules={[
                          {
                            required: true,
                            message: "Please enter select slab",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select Slab"
                          size="large"
                          allowClear
                          showSearch
                          suffixIcon={<SelectDropdownLgIcon />}
                          onChange={handleSlabChange}
                          options={allSalarySlabData?.data?.salarySlabs?.map(
                            (item: any) => ({
                              label: item.slab_name,
                              value: item.id,
                            })
                          )}
                          disabled={
                            singleData?.jobApplication?.is_hired >= 0 &&
                            singleData?.jobApplication
                              ?.applicant_confirm_status !== null
                          }
                        />
                      </Form.Item>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          name="fixed_amount"
                          label="Fixed Amount"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please enter fixed amount",
                            },
                            { validator: validatePositiveNumber },
                          ]}
                        >
                          <Input suffix="BDT" placeholder="0.00" disabled />
                        </Form.Item>
                        <Form.Item
                          name="variable_amount"
                          label="Variable Amount"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please enter variable amount",
                            },
                            { validator: validatePositiveNumber },
                          ]}
                        >
                          <Input suffix="BDT" placeholder="0.00" disabled />
                        </Form.Item>
                      </div>

                      <Form.Item
                        name="kpi_group_id"
                        label="KPI Group"
                        className="mb-4"
                        rules={[
                          {
                            required: true,
                            message: "Please select a KPI group",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select a Group"
                          size="large"
                          allowClear
                          showSearch
                          suffixIcon={<SelectDropdownLgIcon />}
                          // onChange={handleGroupChange}
                          options={
                            allKpiGroupData?.data?.groups?.map((item: any) => ({
                              label: item.group_name,
                              value: item.id,
                            })) || []
                          }
                          disabled={
                            singleData?.jobApplication?.is_hired >= 0 &&
                            singleData?.jobApplication
                              ?.applicant_confirm_status !== null
                          }
                        />
                      </Form.Item>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          name="payment_type"
                          label="Payment Type"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please select payment type",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select Payment Type"
                            size="large"
                            allowClear
                            showSearch
                            suffixIcon={<SelectDropdownLgIcon />}
                            options={[
                              { label: "Monthly", value: "monthly" },
                              { label: "Weekly", value: "weekly" },
                              { label: "Daily", value: "daily" },
                            ]}
                            disabled={
                              singleData?.jobApplication?.is_hired >= 0 &&
                              singleData?.jobApplication
                                ?.applicant_confirm_status !== null
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          name="joining_date"
                          label="Joining Date"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please select joining date",
                            },
                          ]}
                        >
                          <DatePicker
                            className="w-full"
                            format="DD/MM/YYYY"
                            placeholder="dd/mm/yyyy"
                            disabledDate={disablePastDates}
                            disabled={
                              singleData?.jobApplication?.is_hired >= 0 &&
                              singleData?.jobApplication
                                ?.applicant_confirm_status !== null
                            }
                          />
                        </Form.Item>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          name="contract_months"
                          label="Contract (Month)"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please enter contract month",
                            },
                            {
                              validator: (_, value) =>
                                value && value <= 0
                                  ? Promise.reject("Must be positive number")
                                  : Promise.resolve(),
                            },
                          ]}
                        >
                          <Input
                            placeholder="e.g. 5"
                            // onChange={handleContractMonthsChange}
                            allowClear
                            type="number"
                            disabled={
                              singleData?.jobApplication?.is_hired > 0 ||
                              (singleData?.jobApplication?.is_hired >= 0 &&
                                singleData?.jobApplication
                                  ?.applicant_confirm_status !== null)
                            }
                          />
                        </Form.Item>

                        <Form.Item
                          name="contract_end_date"
                          label="Contract End Date"
                          className="mb-4"
                          rules={[
                            {
                              required: true,
                              message: "Please select contract end date",
                            },
                          ]}
                        >
                          <DatePicker
                            className="w-full"
                            format="DD/MM/YYYY"
                            placeholder="dd/mm/yyyy"
                            disabledDate={disablePastDates}
                            disabled
                          />
                        </Form.Item>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="self-stretch justify-start text-slate-700 text-sm font-medium mb-1">
                        Final Price
                      </div>
                      <Input
                        placeholder={
                          (singleData?.jobApplication?.final_price ||
                            "") as string
                        }
                        readOnly
                        disabled
                      />
                    </div>
                  )}
                  {/* Todo: */}
                  <div className="self-stretch justify-center text-slate-700 text-sm font-normal leading-tight tracking-tight mt-4 mb-4">
                    Closing this job marks the end of all ongoing work and
                    communication related to this project. It confirms that both
                    parties have fulfilled their responsibilities, and no
                    further submissions or updates will be accepted. This action
                    helps maintain project clarity, ensures proper
                    record-keeping, and upholds a professional experience across
                    the platform.
                  </div>

                  <Tooltip
                    title={
                      !canClose
                        ? submitted === 0
                          ? "Not submitted task yet"
                          : closed === 1
                          ? "Already closed"
                          : undefined
                        : undefined
                    }
                  >
                    <Button
                      onClick={() => handleOpen({ type: "CLOSE" })}
                      htmlType="submit"
                      size="large"
                      className="w-full"
                      type="primary"
                      disabled={!canClose}
                    >
                      Close the Job
                    </Button>
                  </Tooltip>

                  {!!closed && (
                    <Button
                      htmlType="button"
                      size="large"
                      className="w-full !bg-[#11A75C] !border-[#11A75C] !text-white disabled:!opacity-60 mt-3"
                      disabled={isSendPayment}
                      onClick={() => handleOpen({ type: "PAYMENT" })}
                    >
                      Payment
                    </Button>
                  )}

                  <div className="text-center text-slate-400 text-xs font-medium mt-4">
                    Protected by platform policies
                  </div>
                </Card>
              </Form>
            </div>
          </div>
        </>
      )}
      {open.type === "CLOSE" && (
        <CustomModal
          open={open.open}
          handleClose={handleClose}
          closable={false}
          title={undefined}
          footer={null}
          maskClosable={true}
        >
          <div className="py-2">
            <div className="flex justify-center">
              <img className="h-16 object-contain" src={IMG} alt="" />
            </div>
            <p className="text-center text-black text-base font-normal my-4">
              Congratulations! This project has been successfully completed. By
              closing this job, you confirm that all deliverables have been
              received and both parties are satisfied with the outcome. Thank
              you for maintaining a professional and collaborative environment
              throughout the contract.
            </p>
            <div className="flex justify-center">
              <Button
                size="large"
                className="w-full"
                type="primary"
                onClick={() => handleOpen({ type: "FEEDBACK" })}
              >
                Close & Give Feedback
              </Button>
            </div>
          </div>
        </CustomModal>
      )}
      {open.type === "FEEDBACK" && (
        <CustomModal
          open={open.open}
          handleClose={handleClose}
          closable={false}
          title={undefined}
          footer={null}
          maskClosable={true}
        >
          <div className="pt-2 space-y-4">
            <div className="flex items-start justify-start gap-4">
              <div>
                <img
                  className="w-11 h-11 rounded-[100px]"
                  src={
                    singleData?.jobApplication?.applicant?.profile?.pro_pic ||
                    UserImage
                  }
                  alt="user_image"
                />
              </div>

              <div>
                <div className="flex items-center gap-x-4 flex-wrap">
                  <h4 className="text-base font-medium text-header">
                    {singleData?.jobApplication?.applicant?.name}
                  </h4>
                  {singleData?.jobApplication?.applicant?.is_verified === 1 && (
                    <span className="text-xs font-medium flex items-center gap-1.5 text-primary">
                      <VerifyIcon />
                      Verified
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1 text-sm text-body-text font-medium my-1">
                  <LocationIcon />
                  {singleData?.jobApplication?.applicant?.profile?.district}
                </div>

                <div className="flex items-center flex-wrap gap-x-3">
                  <Rate
                    disabled
                    allowHalf
                    value={0}
                    style={{ color: "#FFC700" }}
                  />

                  {/* Todo: Need to show rating */}
                  {/* <div>
                      <span className="text-header text-base font-medium inline-block mr-1">
                        4.8
                      </span>
                      <span className="text-header text-sm font-normal inline-block">
                        (251)
                      </span>
                    </div> */}
                </div>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              initialValues={{ feedback: "best" }}
              requiredMark={false}
            >
              <Form.Item label="Give a feedback" name="feedback">
                <Radio.Group className="grid grid-cols-2 gap-2">
                  {options.map((option, index) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      className="!w-full !h-auto !p-2 border border-light rounded-lg"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-body-text text-sm">
                          {option.label}
                        </div>
                        <Rate
                          disabled
                          value={option?.rating}
                          className="disabled:cursor-pointer"
                          style={{ fontSize: 16, cursor: "pointer !important" }}
                        />
                      </div>
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
              <Form.Item label="Give a feedback" name="remark">
                <Input.TextArea rows={5} placeholder="Enter a description..." />
              </Form.Item>

              <Form.Item className="!mb-0">
                <div className="flex justify-center">
                  <Button
                    loading={isLoading}
                    size="large"
                    className="w-full"
                    type="primary"
                    htmlType="submit"
                  >
                    Submit
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </div>
        </CustomModal>
      )}
      {open.type === "CONGRATULATIONS" && (
        <CongratulationsModal
          open={open.open}
          handleClose={handleClose}
          closable={false}
          maskClosable={true}
          title={undefined}
          footer={null}
          onOk={() => handleClose()}
          text="This job is closed and marked complete. No further updates can be made. Everything is saved in your dashboard for future review. Thanks for using Bondhu!"
        />
      )}
    </SidebarLayout>
  );
}
